
import 'intersection-observer';
import { Vue, Component, Prop } from 'vue-property-decorator';
import WLProductListItem from './WLProductListItem.vue';
import WLPostCodeOddJobs from './WLPostCodeOddJobs.vue';
import { State, Getter } from 'vuex-class';
import { IDomain } from '@homeserve/od-typings/api';
import { Nullable } from '@homeserve/od-typings/utils';
import { postCodeHelper, traderAvailabilityHelper, Availability } from '@homeserve/vue-api-plugin';
import { WLStickySupportPhone } from '../../components/WLStickySupportPhone';
import { IPostCode, diagnosticHelper } from '../../store';
import { removeAccents } from '../../utils/stringHelper';

// Cf. https://www.npmjs.com/package/intersection-observer#configuring-the-polyfill
(IntersectionObserver.prototype as any).POLL_INTERVAL = 100;

@Component({ components: { WLProductListItem, WLPostCodeOddJobs, WLStickySupportPhone } })
export default class WLHomeFormDiagnosisOddJobs extends Vue {
  public domainErrorMessages = '';
  public postCode: IPostCode = {} as IPostCode;

  @Prop({ type: String, default: '01 70 82 17 82' })
  public supportPhoneNumber!: string;

  @Prop({ type: String, default: '' })
  public phonePicto!: string;

  @Prop({
    type: String,
    default: require('../../assets/img/home-odd-jobs-bg.jpg'),
  })
  public backgroundImg!: string;

  @Getter('postCode', { namespace: 'app/diagnostic' })
  protected postCodeStore!: IPostCode;

  @State('domains', { namespace: 'api/catalog/domain' })
  private domains!: IDomain[];

  @State('domainAvailability', { namespace: 'api/trader/traderAvailability' })
  private domainAvailability!: Availability;

  @State('isEligible', { namespace: 'api/catalog/postCode' })
  private isEligible!: boolean;

  private domain = '';
  private observer: Nullable<IntersectionObserver> = null;
  private postCodeError = false;
  private hasInteracted = false;

  get background() {
    if (!this.$vuetify.breakpoint.xsOnly) {
      return this.backgroundImg;
    }

    return '';
  }

  destroyed() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  get errorMessage() {
    if (this.postCodeError && this.hasInteracted) {
      if (!this.postCode.postCode || this.postCode.postCode.length < 5) {
        return this.$t('components_plugin.diagnosis.error_invalid_postcode').toString();
      } else if (!this.isEligible || (this.domainAvailability && !this.domainAvailability.isAvailable)) {
        return this.$t('components_plugin.diagnosis.error_not_covered_postcode').toString();
      }
    }
    return '';
  }

  created() {
    this.postCode = this.postCodeStore;
  }

  private clearPostCode(event: IPostCode) {
    if (event && event.postCode && event.postCode.length > 0) {
      return;
    }

    this.postCode = {} as IPostCode;
  }

  private async submit() {
    let errors = false;
    this.hasInteracted = true;
    if (!this.domain) {
      this.domainErrorMessages = this.$t('components_plugin.home_odd_jobs.domain_selector_error').toString();
      errors = true;
    }

    if (!this.postCode.postCode || this.postCode.postCode.length < 5) {
      this.postCodeError = true;
      errors = true;
    }

    if (errors) {
      return;
    }

    await postCodeHelper.fetchIsEligible(this.$store, {
      postCode: this.postCode.postCode,
    });

    if (this.isEligible) {
      await traderAvailabilityHelper.fetchDomainAvailability(this.$store, {
        id: this.domain,
        postCode: this.postCode.postCode,
      });

      if (this.domainAvailability.isAvailable) {
        this.sendResultToGTM('google_tag_manager_insider_action');

        await Promise.all([
          diagnosticHelper.setPostCode(this.$store, this.postCode),
          this.$router.push({
            name: 'diagnostic',
            params: { domain: removeAccents(this.domain.toLowerCase()) },
          }),
        ]);
      } else {
        this.sendResultToGTM('google_tag_manager_no_trader_action');
        this.postCodeError = true;
      }
    } else {
      this.sendResultToGTM('google_tag_manager_uncovered_action');
      this.postCodeError = true;
    }
  }

  private sendResultToGTM(slug: string) {
    this.$gtm.trackEvent({
      event: 'eventGA',
      eventCategory: this.$t('components_plugin.diagnosis.google_tag_manager_category_domain', {
        domain: '',
      })
        .toString()
        .trim(),
      eventAction: this.$t(`components_plugin.diagnosis.${slug}`),
      eventLabel: `${this.domain} - ${this.postCode.postCode}`,
    });
  }

  private updateDomain() {
    if (this.domain) {
      this.domainErrorMessages = '';
    }
  }

  private updatePostCode(event: IPostCode) {
    this.hasInteracted = true;
    this.postCode = event;
    this.postCodeError = false;
  }
}
